import { MDXEditor, type MDXEditorMethods } from '@mdxeditor/editor';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  Stack,
  TextField,
  useColorScheme
} from '@mui/material';
import { useEffect, useRef } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';

import type { TemplateDialogPropsType } from '@/pages/Administration/AdministrationTemplates';
import {
  useCreateAITicketTemplates,
  useUpdateAiTicketTemplate
} from '@/pages/AlertRules/AlertRules.store';
import type { TemplateTicketAICreateOrUpdateDTO } from '@/pages/AlertRules/AlertRules.types';
import { MDX_EDITOR_CONFIG } from '@/pages/Ticket/TicketCreate/TicketCreate.constants';

import DEFAULT_AI_TICKET_TEMPLATE_DIALOG_VALUES from './AITicketTemplate.constants';
import useStyles from './AITicketTemplateDialog.styles';

const AiTicketTemplateDialog = (props: TemplateDialogPropsType) => {
  const { open, setOpen, templateType, selectedTemplate } = props;
  const { mode: themeMode } = useColorScheme();
  const ref = useRef<MDXEditorMethods>(null);

  const {
    mutate: createTemplate,
    error: createError,
    isSuccess: createSuccess
  } = useCreateAITicketTemplates();

  const {
    mutate: updateTemplate,
    error: updateError,
    isSuccess: updateSuccess
  } = useUpdateAiTicketTemplate();

  const form = useForm({
    defaultValues: DEFAULT_AI_TICKET_TEMPLATE_DIALOG_VALUES
  });
  const { classes } = useStyles({ errors: form.formState.errors });

  const { control, setValue, getValues, handleSubmit, clearErrors } = form;
  const summary = useWatch({ control, name: 'summary' }) as string;
  const name = useWatch({ control, name: 'name' });

  useEffect(() => {
    setValue('summary', selectedTemplate?.summary as string);
    setValue('name', selectedTemplate?.name as string);
    ref.current?.setMarkdown(selectedTemplate.summary as string);
  }, [selectedTemplate, open]);

  const handleSave = (actionId?: string) => {
    const newTemplateInfo: {
      [key: string]: string | number | object;
    } = {};

    const templateData = {
      summary: getValues('summary') as string,
      name: getValues('name') as string
    };
    newTemplateInfo.data = templateData;

    if (actionId === 'save') {
      newTemplateInfo.id = selectedTemplate.id as number;
      updateTemplate(
        newTemplateInfo as {
          id: number;
          data: TemplateTicketAICreateOrUpdateDTO;
        }
      );
    } else {
      createTemplate(
        newTemplateInfo as { data: TemplateTicketAICreateOrUpdateDTO }
      );
    }
  };

  const handleChange = (value: string) => {
    clearErrors('summary');
    setValue('summary', value, { shouldDirty: true });
  };

  useEffect(() => {
    if (createSuccess) {
      setOpen(false);
    } else if (updateSuccess) {
      setOpen(false);
    }
  }, [createSuccess, updateSuccess]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      fullWidth
      maxWidth="md"
      scroll="paper"
    >
      <DialogTitle>{`${templateType} Template`}</DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(handleSave as () => void)} noValidate>
          <DialogContent>
            <Stack spacing={2}>
              <div style={{ paddingTop: 10 }}>
                <InputLabel htmlFor="name">Template Name *</InputLabel>
                <Controller
                  control={control}
                  name="name"
                  rules={{
                    required: true
                  }}
                  render={({ field, formState }) => (
                    <FormControl fullWidth>
                      <TextField
                        id="name"
                        error={!!formState.errors.name}
                        {...field}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => setValue('name', event.target.value)}
                      />
                    </FormControl>
                  )}
                />
                {createError && typeof createError === 'string' && (
                  <Alert sx={{ mt: 1 }} severity="error">
                    {createError}
                  </Alert>
                )}
                {updateError && typeof updateError === 'string' && (
                  <Alert sx={{ mt: 1 }} severity="error">
                    {updateError}
                  </Alert>
                )}
                <Divider sx={{ marginTop: 3 }} />
              </div>
              <div>
                <InputLabel htmlFor="summary">Summary *</InputLabel>
                <Controller
                  control={control}
                  name="summary"
                  rules={{
                    required: true
                  }}
                  render={() => (
                    <FormControl fullWidth>
                      <div className={classes.summaryErrorBorder}>
                        <MDXEditor
                          className={`${themeMode}-theme dark-editor`}
                          contentEditableClassName="cm-richView"
                          markdown={summary || ''}
                          plugins={MDX_EDITOR_CONFIG}
                          ref={ref}
                          suppressHtmlProcessing
                          onChange={(value) => {
                            handleChange(value);
                          }}
                        />
                      </div>
                    </FormControl>
                  )}
                />
              </div>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              {templateType.includes('New') && (
                <Button
                  disabled={
                    (summary?.length ?? 0) < 5 || (name?.length ?? 0) < 5
                  }
                  type="submit"
                  variant="contained"
                >
                  Create Template
                </Button>
              )}
              {templateType.includes('Edit') && (
                <Button
                  disabled={
                    (summary?.length ?? 0) < 5 || (name?.length ?? 0) < 5
                  }
                  onClick={() => handleSave('save')}
                  variant="contained"
                >
                  Save Changes
                </Button>
              )}
            </Stack>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AiTicketTemplateDialog;
